import fetch from "@lib/fetch";
import sharedConstants from "@lib/shared_constants";

import { integrationsDirectCdiImportSyncsAll } from "../integrationsDirectCdiImportSyncsAll/constants";
import { reactQueryMutationAdapter } from "../reactQueryFetcherAdapters";
import {
  postCdiTestConnectionCacheKey,
  createSyncCacheKey,
  listSyncsCacheKey,
  updateSyncCacheKey,
  postSnowflakeKeyRegenerateCacheKey,
  updateSyncStatusCacheKey,
  postSnowflakeDeleteKeyCacheKey,
  updateSnowflakeKeyCacheKey,
} from "./constants";
import type {
  CdiImportSync,
  CdiTestConnectionResponse,
  PostSnowflakeImportKeyDeleteProps,
  CreateCdiImportSyncParams,
  DeleteCdiImportSyncParams,
  UpdateSnowflakeImportSyncStatusParams,
  CdiImportKey,
  UpdateCdiImportKeyProps,
  CdiErrorResponse,
  CdiKeyRegenerateProps,
  CdiKeyRegenerateResult,
  UpdateCdiImportSyncStatusResponse,
} from "./types";

export const createCdiImportSync = reactQueryMutationAdapter(
  [createSyncCacheKey, integrationsDirectCdiImportSyncsAll],
  (props: CreateCdiImportSyncParams): Promise<CdiImportSync> => {
    return fetch(
      "/integrations/direct/cdi/import-syncs",
      "POST",
      {
        app_group_id: props.appGroupId,
        ...props.importSync,
      },
      {},
      { propagateFetchRequestError: false }
    );
  }
);

export const updateCdiImportSync = reactQueryMutationAdapter(
  [listSyncsCacheKey, updateSyncCacheKey, integrationsDirectCdiImportSyncsAll],
  (props: CreateCdiImportSyncParams): Promise<CdiImportSync | CdiErrorResponse> => {
    return fetch(
      `/integrations/direct/cdi/import-syncs/${props.importSync.integration_id}`,
      "PUT",
      {
        app_group_id: props.appGroupId,
        ...props.importSync,
      },
      {},
      { propagateFetchRequestError: false }
    );
  }
);

export const updateCdiImportSyncStatus = reactQueryMutationAdapter(
  [listSyncsCacheKey, updateSyncStatusCacheKey],
  (props: UpdateSnowflakeImportSyncStatusParams): Promise<UpdateCdiImportSyncStatusResponse> => {
    return fetch(
      `/integrations/direct/cdi/import-syncs/${props.syncId}/status`,
      "PUT",
      {
        status: props.status,
        partner_name: props.partnerName,
      },
      {},
      { propagateFetchRequestError: false }
    );
  }
);

export const deleteCdiImportSync = reactQueryMutationAdapter(
  [listSyncsCacheKey, updateSyncCacheKey],
  (props: DeleteCdiImportSyncParams): Promise<null> => {
    return fetch(
      `/integrations/direct/cdi/import-syncs/${props.syncId}`,
      "DELETE",
      {
        partner_name: props.partnerName,
      },
      {},
      { propagateFetchRequestError: false }
    );
  }
);

export const postCdiTestConnection = reactQueryMutationAdapter(
  postCdiTestConnectionCacheKey,
  (syncId: string): Promise<CdiTestConnectionResponse> => {
    return fetch(
      `/integrations/direct/cdi/import-syncs/${syncId}/connection`,
      "POST",
      {}, //params
      {}, //options
      { setContentType: false, propagateFetchRequestError: false } //settings
    );
  }
);

export const postCdiKeyRegenerate = reactQueryMutationAdapter(
  [postSnowflakeKeyRegenerateCacheKey, listSyncsCacheKey],
  (props: CdiKeyRegenerateProps): Promise<CdiKeyRegenerateResult> => {
    return fetch(
      `/integrations/direct/cdi/import-keys/${props.keyId}/regenerate`,
      "POST",
      { partner_name: props.partnerName },
      {}, //options
      { setContentType: true, propagateFetchRequestError: false } //settings);
    );
  }
);

export const postImportKeysDelete = reactQueryMutationAdapter(
  postSnowflakeDeleteKeyCacheKey,
  (props: PostSnowflakeImportKeyDeleteProps): Promise<any> => {
    return fetch(`/integrations/direct/cdi/import-keys/${props.keyId}/delete`, "POST", {
      partner_name: props.partnerName,
    });
  }
);

export const putImportKeysUpdate = reactQueryMutationAdapter(
  updateSnowflakeKeyCacheKey,
  (props: UpdateCdiImportKeyProps): Promise<CdiImportKey> => {
    // BigQuery and GCS key updates upload a service-account JSON key file, so they
    // must PUT as multipart form data instead of JSON.
    if (
      props.partner_name === sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name ||
      props.partner_name === sharedConstants.DATALAKE.PARTNER_NAME.gcs.name
    ) {
      const body = new FormData();
      for (const key of Object.keys(props) as (keyof UpdateCdiImportKeyProps)[]) {
        if (key === "import_key") {
          body.append(key, JSON.stringify(props[key]));
          continue;
        }
        const value = props[key];
        // `key_file` is optional — skip it rather than posting the string "undefined".
        if (value !== undefined) {
          body.append(key, value);
        }
      }
      return fetch(
        `/integrations/direct/cdi/import-keys/${props.keyId}/update`,
        "PUT",
        {},
        { body },
        { setContentType: false }
      );
    }
    return fetch(`/integrations/direct/cdi/import-keys/${props.keyId}/update`, "PUT", props);
  }
);
